import React, { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';

import { assetCurrencyExchangeApi } from 'api';
import {
  CrudTable,
  CrudTableActionType,
  DataGridColumnDefinition,
  dataGridColumns,
} from 'components';
import { QueryKey } from 'enums';
import { useCurrencies, useMutation } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { AssetCurrencyExchange as AssetCurrencyExchangeType } from 'types';

import { AssetCurrencyExchangeDetailsDialog } from './AssetCurrencyExchangeDetailsDialog';

export const AssetCurrencyExchange: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.currencies.asset_currency_exchange',
  });
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => QueryKey.AssetCurrencyExchange, []);

  const { getAssetCurrencyCode, getCurrencyExchangeLabel } = useCurrencies();

  const queryResult = useQuery(queryKey, assetCurrencyExchangeApi.getAll, {});

  const { mutate: remove } = useMutation(assetCurrencyExchangeApi.remove, {
    onSuccess: () => {
      queryClient.invalidateQueries(queryKey);
    },
    notifierType: 'remove',
  });

  const columns = useMemo(
    (): DataGridColumnDefinition<AssetCurrencyExchangeType>[] => [
      dataGridColumns.getIdColumn(),
      {
        header: t('fields.name'),
        valueKey: 'name',
      },
      {
        header: t('fields.currencies'),
        valueGetter: (item) =>
          `${getAssetCurrencyCode(
            item.assetCurrencyId,
          )} / ${getAssetCurrencyCode(item.crossAssetCurrencyId)}`,
      },
      {
        header: t('fields.currency_exchange'),
        valueGetter: (item) =>
          getCurrencyExchangeLabel(
            item.currencyExchange?.assetCurrencyId,
            item.currencyExchange?.fiatCurrencyId,
            item.currencyExchange?.source,
            item.currencyExchange?.name,
          ),
      },
      {
        header: t('fields.params'),
        valueGetter: (item) => (
          <Fragment>
            {item.sellExchange && <div>{t('fields.sell_exchange')}</div>}
            {item.buyExchange && <div>{t('fields.buy_exchange')}</div>}
            {item.statisticExchange && (
              <div>{t('fields.statistic_exchange')}</div>
            )}
          </Fragment>
        ),
      },
    ],
    [getAssetCurrencyCode, getCurrencyExchangeLabel, t],
  );

  return (
    <CrudTable
      queryResult={queryResult}
      columns={columns}
      actions={[
        {
          type: CrudTableActionType.Details,
          renderDialog: AssetCurrencyExchangeDetailsDialog,
        },
        {
          type: CrudTableActionType.Remove,
          onRemove: (item, { close }) => remove(item.id, { onSuccess: close }),
        },
      ]}
    />
  );
};
